import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import { ROUTES } from '../../../utils/constants';
import { formatDate } from '../../../utils/helpers';
import {
  getShiftWithRegistrations,
  checkInVolunteer,
  checkOutVolunteer,
  adminRegisterForShift,
  getVolunteers,
} from '../../../services/volunteerApi';
import { VolunteerShift, VolunteerShiftRegistration, VolunteerUser } from '../../../types/volunteer';
import { volunteerErrorMessage, STAFF_REFUSALS } from '../../../utils/volunteerErrors';

const VolunteerShiftAttendeesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shift, setShift] = useState<VolunteerShift | null>(null);
  const [registrations, setRegistrations] = useState<VolunteerShiftRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Check-out dialog state
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [checkOutRegId, setCheckOutRegId] = useState<string | null>(null);
  const [checkOutNotes, setCheckOutNotes] = useState('');

  // Register volunteer dialog state
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [approvedVolunteers, setApprovedVolunteers] = useState<VolunteerUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);

  const fetchShiftData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getShiftWithRegistrations(id);
      setShift(res.data.shift || res.data);
      setRegistrations(res.data.registrations || []);
    } catch (err) {
      console.error('Failed to fetch shift:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchShiftData();
  }, [fetchShiftData]);

  const handleCheckIn = async (registrationId: string) => {
    if (!id) return;
    try {
      await checkInVolunteer(id, registrationId);
      setSnackbar({ open: true, message: 'Volunteer checked in successfully', severity: 'success' });
      fetchShiftData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to check in volunteer', severity: 'error' });
    }
  };

  const handleCheckOutClick = (registrationId: string) => {
    setCheckOutRegId(registrationId);
    setCheckOutNotes('');
    setCheckOutDialogOpen(true);
  };

  const handleCheckOutConfirm = async () => {
    if (!id || !checkOutRegId) return;
    try {
      await checkOutVolunteer(id, checkOutRegId, checkOutNotes || undefined);
      setSnackbar({ open: true, message: 'Volunteer checked out successfully', severity: 'success' });
      setCheckOutDialogOpen(false);
      setCheckOutRegId(null);
      setCheckOutNotes('');
      fetchShiftData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to check out volunteer', severity: 'error' });
    }
  };

  const handleOpenRegisterDialog = async () => {
    setRegisterDialogOpen(true);
    try {
      const res = await getVolunteers({ status: 'approved' });
      setApprovedVolunteers(res.data);
    } catch (err) {
      console.error('Failed to fetch approved volunteers:', err);
    }
  };

  const handleRegisterVolunteer = async () => {
    if (!id || !selectedUserId) return;
    setIsRegistering(true);
    try {
      await adminRegisterForShift(id, selectedUserId, selectedRoleId || undefined);
      setSnackbar({ open: true, message: 'Volunteer registered successfully', severity: 'success' });
      setRegisterDialogOpen(false);
      setSelectedUserId('');
      setSelectedRoleId('');
      setSearchEmail('');
      fetchShiftData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: volunteerErrorMessage(err, 'Failed to register volunteer', STAFF_REFUSALS),
        severity: 'error',
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const filteredApprovedVolunteers = approvedVolunteers.filter((v) =>
    v.email.toLowerCase().includes(searchEmail.toLowerCase()) ||
    `${v.firstName} ${v.lastName}`.toLowerCase().includes(searchEmail.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shift) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Shift not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.ADMIN_VOLUNTEER_SHIFTS)}>
          Back to Shifts
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.ADMIN_VOLUNTEER_SHIFTS)}
        sx={{ mb: 3 }}
      >
        Back to Shifts
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {shift.title} - Attendees
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage volunteer check-ins and registrations
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenRegisterDialog}
        >
          Register Volunteer
        </Button>
      </Box>

      {/* Shift Details Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {formatDate(shift.date)} | {shift.startTime} - {shift.endTime}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {shift.location}
                {shift.postcode && ` (${shift.postcode})`}
              </Typography>
              {shift.postcode && (
                <Button
                  size="small"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shift.postcode)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<MapIcon />}
                  sx={{ mt: 0.5, p: 0, minWidth: 'auto' }}
                >
                  View on Map
                </Button>
              )}
              {shift.event && (
                <Chip label={`Event: ${shift.event.title}`} size="small" variant="outlined" sx={{ mt: 1 }} />
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" sx={{ color: '#00b7ed' }} fontWeight={700}>
                {shift.currentVolunteers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                of {shift.capacity} volunteers
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Registrations Table */}
      {registrations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">No volunteers registered for this shift</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Check-in Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Check-in Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Check-out Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registrations.map((reg) => (
                <TableRow key={reg.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>
                      {reg.user?.firstName} {reg.user?.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>{reg.user?.email || '-'}</TableCell>
                  <TableCell>
                    {reg.role ? (
                      <Chip label={reg.role.name} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {reg.checkOutTime ? (
                      <Chip icon={<CheckIcon />} label="Completed" size="small" color="success" />
                    ) : reg.checkedIn ? (
                      <Chip icon={<CheckIcon />} label="Checked In" size="small" color="info" />
                    ) : reg.status === 'cancelled' ? (
                      <Chip label="Cancelled" size="small" color="error" />
                    ) : (
                      <Chip label="Not arrived" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {reg.checkInTime
                      ? new Date(reg.checkInTime).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {reg.checkOutTime
                      ? new Date(reg.checkOutTime).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {reg.totalHours != null ? `${reg.totalHours.toFixed(1)}h` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {reg.status !== 'cancelled' && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {!reg.checkedIn && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckIcon />}
                            onClick={() => handleCheckIn(reg.id)}
                          >
                            Check In
                          </Button>
                        )}
                        {reg.checkedIn && !reg.checkOutTime && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<CloseIcon />}
                            onClick={() => handleCheckOutClick(reg.id)}
                          >
                            Check Out
                          </Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Check-Out Dialog */}
      <Dialog
        open={checkOutDialogOpen}
        onClose={() => setCheckOutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Check Out Volunteer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally add notes about this volunteer's shift.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={checkOutNotes}
            onChange={(e) => setCheckOutNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCheckOutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCheckOutConfirm}>
            Check Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register Volunteer Dialog */}
      <Dialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Register Volunteer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for an approved volunteer by name or email to register them for this shift.
          </Typography>
          <TextField
            fullWidth
            label="Search by name or email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            select
            label="Select Volunteer"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select a volunteer</MenuItem>
            {filteredApprovedVolunteers.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.firstName} {v.lastName} ({v.email})
              </MenuItem>
            ))}
          </TextField>
          {shift.roles.length > 0 && (
            <TextField
              fullWidth
              select
              label="Role (optional)"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            >
              <MenuItem value="">No specific role</MenuItem>
              {shift.roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name} {role.capacity ? `(${role.capacity} spots)` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRegisterDialogOpen(false)} disabled={isRegistering}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRegisterVolunteer}
            disabled={!selectedUserId || isRegistering}
          >
            {isRegistering ? 'Registering...' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VolunteerShiftAttendeesPage;
